import {
  Check,
  AlertTriangle,
  Sparkles,
  ArrowUpDown,
  AlertCircle,
} from "lucide-react";
import { Tonal } from "@/components/ui/tonal";
import type { ComparisonFinding, FindingsFilter } from "./comparison-types";
import { ComparisonFindingRow } from "./comparison-finding-row";
import { FindingsFilterRibbon } from "./findings-filter-ribbon";

export interface FindingsDeltaViewProps {
  newProblems: ComparisonFinding[];
  newStrengths: ComparisonFinding[];
  resolvedFindings: ComparisonFinding[];
  lostStrengths: ComparisonFinding[];
  changedSeverity: Array<{
    base: ComparisonFinding;
    compare: ComparisonFinding;
  }>;
  persistentFindings: Array<{
    base: ComparisonFinding;
    compare: ComparisonFinding;
  }>;
  findingsFilter: FindingsFilter;
  setFindingsFilter: (filter: FindingsFilter) => void;
  totalDiffs: number;
  resolvedCount: number;
  changedCount: number;
  persistentCount: number;
  baseRunNumber?: number;
  currentRunNumber?: number;
  canEdit: boolean;
  isTrackerConfigured?: boolean;
  onBulkCreateIssues?: (ids: string[]) => void;
  onOpenFinding: (id: string) => void;
  onCreateIssue?: (canonicalId: string | null) => void;
  onLinkIssue?: (canonicalId: string) => void;
}

export function FindingsDeltaView({
  newProblems,
  newStrengths,
  resolvedFindings,
  lostStrengths,
  changedSeverity,
  persistentFindings,
  findingsFilter,
  setFindingsFilter,
  totalDiffs,
  resolvedCount,
  changedCount,
  persistentCount,
  baseRunNumber,
  currentRunNumber,
  canEdit,
  isTrackerConfigured,
  onBulkCreateIssues,
  onOpenFinding,
  onCreateIssue,
  onLinkIssue,
}: FindingsDeltaViewProps) {
  return (
    <div className="space-y-5">
      {/* Filter Ribbon Strip */}
      <FindingsFilterRibbon
        findingsFilter={findingsFilter}
        setFindingsFilter={setFindingsFilter}
        totalDiffs={totalDiffs}
        newProblemsCount={newProblems.length}
        newStrengthsCount={newStrengths.length}
        resolvedCount={resolvedCount}
        lostStrengthsCount={lostStrengths.length}
        changedCount={changedCount}
        persistentCount={persistentCount}
        canEdit={canEdit}
        isTrackerConfigured={isTrackerConfigured}
        onBulkCreateIssues={
          onBulkCreateIssues
            ? () =>
                onBulkCreateIssues(
                  newProblems
                    .map((f) => f.canonicalFindingId)
                    .filter((id): id is string => Boolean(id)),
                )
            : undefined
        }
      />

      {/* Clean State (Zero Differences) */}
      {totalDiffs === 0 && (
        <Tonal
          padding="md"
          className="text-center rounded-lg border border-border/80 space-y-1 py-8"
        >
          <Check className="size-6 text-emerald-500 mx-auto mb-1" />
          <p className="text-sm font-semibold text-foreground">
            No finding deltas between Run #{baseRunNumber} and Run #
            {currentRunNumber}
          </p>
          <p className="text-xs text-muted-foreground">
            All {persistentCount} findings persisted with identical status and
            severity.
          </p>
        </Tonal>
      )}

      {/* Section 1: New Regressions & Problems */}
      {(findingsFilter === "all" || findingsFilter === "new") &&
        newProblems.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-destructive">
              <AlertTriangle className="size-3.5" />
              <span>New problems and regressions ({newProblems.length})</span>
              <span className="text-xs text-muted-foreground font-normal">
                — introduced in Run #{currentRunNumber}
              </span>
            </div>

            <div className="divide-y divide-border/60 border-t border-border/60">
              {newProblems.map((f) => (
                <ComparisonFindingRow
                  key={f.id}
                  finding={f}
                  variant="new_problem"
                  onClick={() => onOpenFinding(f.id)}
                  canEdit={canEdit}
                  isTrackerConfigured={isTrackerConfigured}
                  onCreateIssue={onCreateIssue}
                  onLinkIssue={onLinkIssue}
                />
              ))}
            </div>
          </div>
        )}

      {/* Section 1b: New Strengths */}
      {(findingsFilter === "all" || findingsFilter === "strengths") &&
        newStrengths.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
              <Sparkles className="size-3.5" />
              <span>New strengths ({newStrengths.length})</span>
              <span className="text-xs text-muted-foreground font-normal">
                — confirmed working in Run #{currentRunNumber}
              </span>
            </div>

            <div className="divide-y divide-border/60 border-t border-border/60">
              {newStrengths.map((f) => (
                <ComparisonFindingRow
                  key={f.id}
                  finding={f}
                  variant="new_strength"
                  onClick={() => onOpenFinding(f.id)}
                />
              ))}
            </div>
          </div>
        )}

      {/* Section 2: Resolved Findings */}
      {(findingsFilter === "all" || findingsFilter === "resolved") &&
        resolvedFindings.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
              <Check className="size-3.5" />
              <span>Resolved findings ({resolvedFindings.length})</span>
              <span className="text-xs text-muted-foreground font-normal">
                — verified fixed since Run #{baseRunNumber}
              </span>
            </div>

            <div className="divide-y divide-border/60 border-t border-border/60">
              {resolvedFindings.map((f) => (
                <ComparisonFindingRow
                  key={f.id}
                  finding={f}
                  variant="resolved"
                  previousSeverity={f.severity}
                  onClick={() => onOpenFinding(f.id)}
                />
              ))}
            </div>
          </div>
        )}

      {/* Section 2b: Strengths Lost */}
      {(findingsFilter === "all" || findingsFilter === "lost_strengths") &&
        lostStrengths.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 dark:text-amber-400">
              <AlertTriangle className="size-3.5" />
              <span>Strengths lost ({lostStrengths.length})</span>
              <span className="text-xs text-muted-foreground font-normal">
                — seen in Run #{baseRunNumber}, not seen in Run #
                {currentRunNumber}
              </span>
            </div>

            <div className="divide-y divide-border/60 border-t border-border/60">
              {lostStrengths.map((f) => (
                <ComparisonFindingRow
                  key={f.id}
                  finding={f}
                  variant="lost_strength"
                  onClick={() => onOpenFinding(f.id)}
                />
              ))}
            </div>
          </div>
        )}

      {/* Section 3: Severity Shifts */}
      {(findingsFilter === "all" || findingsFilter === "changed") &&
        changedSeverity.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 dark:text-amber-400">
              <ArrowUpDown className="size-3.5" />
              <span>Severity shifts ({changedSeverity.length})</span>
              <span className="text-xs text-muted-foreground font-normal">
                — Run #{baseRunNumber} → Run #{currentRunNumber}
              </span>
            </div>

            <div className="divide-y divide-border/60 border-t border-border/60">
              {changedSeverity.map(({ base, compare }) => (
                <ComparisonFindingRow
                  key={compare.id}
                  finding={compare}
                  variant="shifted"
                  previousSeverity={base.severity}
                  newSeverity={compare.severity}
                  onClick={() => onOpenFinding(compare.id)}
                />
              ))}
            </div>
          </div>
        )}

      {/* Section 4: Persistent Findings */}
      {(findingsFilter === "all" || findingsFilter === "persisting") &&
        persistentFindings.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
              <AlertCircle className="size-3.5" />
              <span>Persisting findings ({persistentFindings.length})</span>
              <span className="text-xs text-muted-foreground font-normal">
                — unchanged across both runs
              </span>
            </div>

            <div className="divide-y divide-border/60 border-t border-border/60">
              {persistentFindings.map(({ compare }) => (
                <ComparisonFindingRow
                  key={compare.id}
                  finding={compare}
                  variant="persisting"
                  onClick={() => onOpenFinding(compare.id)}
                  canEdit={canEdit}
                  isTrackerConfigured={isTrackerConfigured}
                  onCreateIssue={onCreateIssue}
                  onLinkIssue={onLinkIssue}
                />
              ))}
            </div>
          </div>
        )}
    </div>
  );
}
